/**
 * Template data conversion utilities
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import { normalizeShapeType, isMarkerId } from "../../../util/shapeTypeMapper";

/**
 * Convert API shapes format to MyCanvas format
 * @param {Array} shapes - Array of shape objects from API
 * @param {Object} pageData - Optional page data containing layout_config for fallback
 * @returns {Object} MyCanvas format object with arrays for each shape type
 */
export const convertApiShapesToMyCanvasFormat = (
  shapes = [],
  pageData = null
) => {
  const myCanvasFormat = {
    texts: [],
    rectangles: [],
    circles: [],
    arrows: [],
    lines: [],
    doubleSidedArrows: [],
    images: [],
    scribbles: [],
    zoomLevel: 100,
    canvasHeight: 100, // Default canvas height
  };

  shapes.forEach((shape) => {
    // Normalize the shape type first
    const normalizedType = normalizeShapeType(shape.type);
    const shapeProperties = shape.shape_properties || {};
    const shapeInfo = shape.shape_info || {};
    // Merge both payload branches so text metadata is not dropped
    // when API sends richer fields only in shape_info.
    const shapeData = {
      ...shapeInfo,
      ...Object.fromEntries(
        Object.entries(shapeProperties).filter(
          ([, value]) => value !== null && value !== undefined
        )
      ),
    };

    switch (normalizedType) {
      case "Text": {
        // CRITICAL: Skip shapes that only have a marker id (from layout_config / parts) - API returns 2 texts, never create a 3rd from marker
        if (isMarkerId(shape.shape_id) || (!shape.shape_id && shapeData.id && isMarkerId(shapeData.id))) {
          break;
        }
        // CRITICAL: Prioritize shape_id from API over id from shape_properties
        // shape_id is the authoritative ID from the database
        // shape_properties.id may contain marker IDs (marker- or marker_) that should be ignored
        const textId =
          shape.shape_id ||
          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
          `text_${Date.now()}_${Math.random()}`;
        // Default strokeWidth to 1 if not present in API response for text elements
        const strokeWidth = shapeData.strokeWidth !== undefined && shapeData.strokeWidth !== null
          ? shapeData.strokeWidth
          : (shapeData.stroke_size !== undefined && shapeData.stroke_size !== null
            ? shapeData.stroke_size
            : 1);
        // CRITICAL: Position and size only from this shape (shape-level or shape_properties), never from shape_info to avoid merging another shape's x,y
        const shapeLocation = shape.canvas_page_location_and_size || shapeData.canvas_page_location_and_size;
        const x = shapeData.x ?? shapeLocation?.x ?? 0;
        const y = shapeData.y ?? shapeLocation?.y ?? 0;
        const width = shapeData.width ?? shapeLocation?.width ?? 200;
        const height = shapeData.height ?? shapeLocation?.height ?? 100;
        // Omit shape_info from spread - API can nest another shape's data there (wrong position/content)
        const { shape_info: nestedShapeInfo, ...shapeDataFlat } = shapeData;
        const partsByTypeFromShape =
          shape.partsByType ??
          shapeProperties.partsByType ??
          shapeInfo.partsByType ??
          shapeData.partsByType ??
          nestedShapeInfo?.partsByType;
        // Build text object with identity and position first so they are never overwritten by spread
        const textObj = {
          id: textId,
          shape_id: shape.shape_id,
          x,
          y,
          width,
          height,
          partsByType: partsByTypeFromShape,
          content: shapeData.content,
          task: shapeData.task,
          note: shapeData.note,
          name: shapeData.name || "Text Box",
          strokeWidth,
          ...(shapeLocation && { canvas_page_location_and_size: { ...shapeLocation, x, y, width, height } }),
        };
        // Spread only non-position/non-identity fields from shapeData to avoid overwriting x,y,id,shape_id with another shape's data
        Object.keys(shapeDataFlat).forEach((k) => {
          if (!["id", "x", "y", "width", "height", "shape_id", "canvas_page_location_and_size"].includes(k)) {
            textObj[k] = shapeDataFlat[k];
          }
        });
        myCanvasFormat.texts.push(textObj);
        break;
      }
      case "Circle":
        // CRITICAL: Prioritize shape_id from API over id from shape_properties
        // shape_id is the authoritative ID from the database
        // shape_properties.id may contain marker IDs that should be ignored
        const circleId =
          shape.shape_id ||
          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
          `circle_${Date.now()}_${Math.random()}`;
        myCanvasFormat.circles.push({
          x: shapeData.x || 0,
          y: shapeData.y || 0,
          radius: shapeData.radius || 50,
          fill: shapeData.fill || shapeData.fillColor || "#ffffff",
          stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
          strokeWidth: shapeData.strokeWidth || 2,
          name: shapeData.name || "Circle",
          ...shapeData,
          id: circleId,
          shape_id: shape.shape_id,
        });
        break;
      case "Rect":
      case "rectangle": {
        // Handle rectangles - extract properties from shape_properties
        // If shape_properties is null, try to get data from layout_config
        let rectData = shapeData;

        // If shape_properties is null or empty, try to find in layout_config
        if (
          (!shape.shape_properties || Object.keys(shapeData).length === 0) &&
          pageData?.layout_config
        ) {
          const layout = pageData.layout_config.layout;
          if (layout?.columns) {
            for (const column of layout.columns) {
              if (column.elements) {
                const layoutElement = column.elements.find(
                  (el) =>
                    el.id === shape.shape_id ||
                    el.type === "rectangle" ||
                    (el.type === normalizedType && el.size)
                );
                if (layoutElement) {
                  rectData = {
                    x: layoutElement.position?.x || 0,
                    y: layoutElement.position?.y || 0,
                    width: layoutElement.size?.width || 100,
                    height: layoutElement.size?.height || 100,
                    fill: layoutElement.style?.fillColor || "#ffffff",
                    stroke: layoutElement.style?.strokeColor || "#000000",
                    strokeWidth: 2,
                    name: "Rectangle",
                  };
                  break;
                }
              }
            }
          }
        }

        // If we still don't have data, skip this rectangle (it's likely a marker placeholder)
        if (
          !rectData ||
          (Object.keys(rectData).length === 0 && !shape.shape_properties)
        ) {
          console.warn("⚠️ Skipping rectangle with no data:", shape.shape_id);
          break;
        }

        // CRITICAL: Prioritize shape_id from API over id from shape_properties
        // shape_id is the authoritative ID from the database
        // shape_properties.id may contain marker IDs that should be ignored
        const rectId =
          shape.shape_id ||
          (rectData.id && !isMarkerId(rectData.id) ? rectData.id : null) ||
          `rect_${Date.now()}_${Math.random()}`;
        // Ensure we have required properties
        const rectFinal = {
          x: rectData.x || 0,
          y: rectData.y || 0,
          width: rectData.width || 100,
          height: rectData.height || 100,
          fill: rectData.fill || rectData.fillColor || "#ffffff",
          stroke: rectData.stroke || rectData.strokeColor || "#000000",
          strokeWidth: rectData.strokeWidth || 2,
          name: rectData.name || "Rectangle",
          ...rectData,
          id: rectId,
          // Preserve shape_id for tracking
          shape_id: shape.shape_id,
        };

        // Only add if it has valid dimensions
        if (rectFinal.width > 0 && rectFinal.height > 0) {
          myCanvasFormat.rectangles.push(rectFinal);
        }
        break;
      }
      case "Arrow": {
        // Normalize to invariant: store points RELATIVE to (x,y)
        const arrowX = Number.isFinite(shapeData.x) ? shapeData.x : 0;
        const arrowY = Number.isFinite(shapeData.y) ? shapeData.y : 0;
        const incoming = Array.isArray(shapeData.points)
          ? shapeData.points
          : [0, 0, 100, 100];
        
        // Treat points as absolute only when first point matches element x/y.
        // Relative points may start at non-zero local offsets.
        const firstPointX = incoming[0] || 0;
        const firstPointY = incoming[1] || 0;
        const isAbsoluteLike =
          Math.abs(firstPointX - arrowX) < 0.1 &&
          Math.abs(firstPointY - arrowY) < 0.1;
        
        // If incoming are absolute, convert to relative; if already relative, leave as-is
        const relativePoints = [];
        if (!isAbsoluteLike) {
          // Points are already relative, use them as-is
          relativePoints.push(...incoming);
        } else {
          // Points are absolute, convert to relative
          for (let i = 0; i < incoming.length; i += 2) {
            const absX = incoming[i];
            const absY = incoming[i + 1];
            relativePoints.push((absX || 0) - arrowX, (absY || 0) - arrowY);
          }
        }


        // CRITICAL: Prioritize shape_id from API over id from shape_properties
        // shape_id is the authoritative ID from the database
        // shape_properties.id may contain marker IDs that should be ignored
        const arrowId =
          shape.shape_id ||
          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
          `arrow_${Date.now()}_${Math.random()}`;
        // CRITICAL: For Arrow types, set arrow head properties
        // hasEndArrow should default to true for Arrow type
        const hasStartArrow = shapeData.hasStartArrow !== undefined
          ? shapeData.hasStartArrow
          : shapeData.pointerAtBeginning || false;
        // For Arrow types, hasEndArrow should default to true unless explicitly false
        const hasEndArrow = shapeData.pointerAtEnd === false
          ? false
          : (shapeData.hasEndArrow !== undefined
            ? shapeData.hasEndArrow
            : true); // Default to true for Arrow type
        myCanvasFormat.arrows.push({
          ...shapeData,
          x: arrowX,
          y: arrowY,
          points: relativePoints,
          stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
          strokeWidth: shapeData.strokeWidth || 2,
          fill: shapeData.fill || shapeData.fillColor || "#000000",
          name: shapeData.name || "Arrow",
          strokeColor: shapeData.stroke || shapeData.strokeColor || "#000000",
          hasStartArrow: Boolean(hasStartArrow),
          hasEndArrow: Boolean(hasEndArrow),
          type: "Arrow", // CRITICAL: Set type to Arrow
          id: arrowId,
          shape_id: shape.shape_id,
        });
        break;
      }
      case "Line": {
        // CRITICAL: API may return top-level type "Line" but shape_properties.type "Arrow" with hasEndArrow: true.
        // Use shape_properties.type when present so we route to the correct array (arrows/doubleSidedArrows) for edit-mode canvas.
        const effectiveLineType = normalizeShapeType(shapeData.type) || normalizedType;

        // Convert absolute coordinates to relative coordinates
        const lineX = shapeData.x || 0;
        const lineY = shapeData.y || 0;
        const absoluteLinePoints = shapeData.points || [0, 0, 100, 100];

        // Treat points as absolute only when first point matches element x/y.
        const firstPointX = absoluteLinePoints[0] || 0;
        const firstPointY = absoluteLinePoints[1] || 0;
        const isAbsoluteLike =
          Math.abs(firstPointX - lineX) < 0.1 &&
          Math.abs(firstPointY - lineY) < 0.1;

        // Convert absolute points to relative points
        const relativeLinePoints = [];
        if (!isAbsoluteLike) {
          // Points are already relative, use them as-is
          relativeLinePoints.push(...absoluteLinePoints);
        } else {
          // Points are absolute, convert to relative
          for (let i = 0; i < absoluteLinePoints.length; i += 2) {
            const absX = absoluteLinePoints[i];
            const absY = absoluteLinePoints[i + 1];
            const relX = absX - lineX;
            const relY = absY - lineY;
            relativeLinePoints.push(relX, relY);
          }
        }

        // CRITICAL: Prioritize shape_id from API over id from shape_properties
        // shape_id is the authoritative ID from the database
        // shape_properties.id may contain marker IDs that should be ignored
        const lineId =
          shape.shape_id ||
          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
          `line_${Date.now()}_${Math.random()}`;

        if (effectiveLineType === "Arrow") {
          // Top-level was "Line" but shape_properties says Arrow – push to arrows so edit-mode canvas shows arrow head
          const hasStartArrow = shapeData.hasStartArrow !== undefined
            ? shapeData.hasStartArrow
            : shapeData.pointerAtBeginning || false;
          const hasEndArrow = shapeData.pointerAtEnd === false
            ? false
            : (shapeData.hasEndArrow !== undefined ? shapeData.hasEndArrow : true);
          myCanvasFormat.arrows.push({
            ...shapeData,
            x: lineX,
            y: lineY,
            points: relativeLinePoints,
            stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
            strokeWidth: shapeData.strokeWidth || 2,
            fill: shapeData.fill || shapeData.fillColor || "#000000",
            name: shapeData.name || "Arrow",
            strokeColor: shapeData.stroke || shapeData.strokeColor || "#000000",
            hasStartArrow: Boolean(hasStartArrow),
            hasEndArrow: Boolean(hasEndArrow),
            type: "Arrow",
            startDot: shapeData.startDot,
            endDot: shapeData.endDot,
            id: lineId,
            shape_id: shape.shape_id,
          });
        } else if (effectiveLineType === "DoubleSidedArrow") {
          // Top-level was "Line" but shape_properties says DoubleSidedArrow – push to doubleSidedArrows
          const hasStartArrow = shapeData.hasStartArrow !== undefined
            ? shapeData.hasStartArrow
            : shapeData.pointerAtBeginning !== undefined ? shapeData.pointerAtBeginning : true;
          const hasEndArrow = shapeData.hasEndArrow !== undefined
            ? shapeData.hasEndArrow
            : shapeData.pointerAtEnd !== undefined ? shapeData.pointerAtEnd : true;
          myCanvasFormat.doubleSidedArrows.push({
            ...shapeData,
            x: lineX,
            y: lineY,
            points: relativeLinePoints,
            stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
            strokeWidth: shapeData.strokeWidth || 2,
            fill: shapeData.fill || shapeData.fillColor || "#000000",
            name: shapeData.name || "Double Sided Arrow",
            strokeColor: shapeData.stroke || shapeData.strokeColor || "#000000",
            hasStartArrow: Boolean(hasStartArrow),
            hasEndArrow: Boolean(hasEndArrow),
            type: "DoubleSidedArrow",
            startDot: shapeData.startDot,
            endDot: shapeData.endDot,
            id: lineId,
            shape_id: shape.shape_id,
          });
        } else {
          myCanvasFormat.lines.push({
            ...shapeData,
            x: lineX,
            y: lineY,
            points: relativeLinePoints,
            stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
            strokeWidth: shapeData.strokeWidth || 2,
            startDot: shapeData.startDot,
            endDot: shapeData.endDot,
            name: shapeData.name || "Line",
            id: lineId,
            shape_id: shape.shape_id,
          });
        }
        break;
      }
      case "DoubleSidedArrow": {
        const doubleArrowX = Number.isFinite(shapeData.x) ? shapeData.x : 0;
        const doubleArrowY = Number.isFinite(shapeData.y) ? shapeData.y : 0;
        const incoming = Array.isArray(shapeData.points)
          ? shapeData.points
          : [0, 0, 100, 100];
        
        // Treat points as absolute only when first point matches element x/y.
        const firstPointX = incoming[0] || 0;
        const firstPointY = incoming[1] || 0;
        const isAbsoluteLike =
          Math.abs(firstPointX - doubleArrowX) < 0.1 &&
          Math.abs(firstPointY - doubleArrowY) < 0.1;
        
        const relativePoints = [];
        if (!isAbsoluteLike) {
          // Points are already relative, use them as-is
          relativePoints.push(...incoming);
        } else {
          // Points are absolute, convert to relative
          for (let i = 0; i < incoming.length; i += 2) {
            const absX = incoming[i];
            const absY = incoming[i + 1];
            relativePoints.push(
              (absX || 0) - doubleArrowX,
              (absY || 0) - doubleArrowY
            );
          }
        }


        // CRITICAL: Prioritize shape_id from API over id from shape_properties
        // shape_id is the authoritative ID from the database
        // shape_properties.id may contain marker IDs that should be ignored
        const doubleArrowId =
          shape.shape_id ||
          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
          `double_arrow_${Date.now()}_${Math.random()}`;
        myCanvasFormat.doubleSidedArrows.push({
          ...shapeData,
          x: doubleArrowX,
          y: doubleArrowY,
          points: relativePoints,
          stroke: shapeData.stroke || shapeData.strokeColor || "#000000",
          strokeWidth: shapeData.strokeWidth || 2,
          fill: shapeData.fill || shapeData.fillColor || "#000000",
          name: shapeData.name || "Double Sided Arrow",
          strokeColor: shapeData.stroke || shapeData.strokeColor || "#000000",
          id: doubleArrowId,
          shape_id: shape.shape_id,
        });
        break;
      }
      case "Image": {
        const imageId =
          shape.shape_id ||
          (shapeData.id && !isMarkerId(shapeData.id) ? shapeData.id : null) ||
          `image_${Date.now()}_${Math.random()}`;
        const imgSrc =
          shapeData.src ||
          shapeData.imageDataUrl ||
          shapeData.image_path ||
          shapeData.imagePath ||
          "";
        if (!imgSrc && !shapeData.referenceUrl) {
          console.warn("⚠️ Skipping image with no src:", shape.shape_id);
          break;
        }
        myCanvasFormat.images.push({
          x: shapeData.x ?? 0,
          y: shapeData.y ?? 0,
          width: shapeData.width ?? 100,
          height: shapeData.height ?? 100,
          src: imgSrc || shapeData.referenceUrl || "",
          name: shapeData.name || "Image",
          rotation: shapeData.rotation ?? 0,
          scaleX: shapeData.scaleX ?? 1,
          scaleY: shapeData.scaleY ?? 1,
          id: imageId,
          shape_id: shape.shape_id,
          image_id: imageId,
          ...shapeData,
        });
        break;
      }
      default:
        console.warn(
          `⚠️ Unrecognized shape type: ${shape.type} (normalized: ${normalizedType})`,
          { shape }
        );
    }
  });


  return myCanvasFormat;
};
